import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/reviews`, {
        withCredentials: true
      });
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/reviews/${id}`, {
        withCredentials: true
      });
      setReviews(reviews.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Failed to delete review');
    }
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl font-bold">Reviews</h1>
        <Link
          to="/admin/reviews/create"
          data-testid="create-review-btn"
          className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:opacity-90 smooth-transition"
        >
          <Plus size={20} />
          <span>Create Review</span>
        </Link>
      </div>

      <div className="bg-card border border-border">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Title</th>
              <th className="text-left p-4 font-semibold">Product</th>
              <th className="text-left p-4 font-semibold">Category</th>
              <th className="text-left p-4 font-semibold">Rating</th>
              <th className="text-left p-4 font-semibold">Status</th>
              <th className="text-left p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-muted-foreground">
                  No reviews yet. Create your first review!
                </td>
              </tr>
            ) : (
              reviews.map((review) => (
                <tr key={review.id} data-testid={`review-row-${review.id}`} className="border-b border-border hover:bg-muted">
                  <td className="p-4 font-medium">{review.title}</td>
                  <td className="p-4">{review.product_name}</td>
                  <td className="p-4">{review.category?.name}</td>
                  <td className="p-4">{review.rating}/5</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 text-xs rounded-sm font-medium ${
                      review.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {review.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/admin/reviews/${review.id}/edit`}
                        data-testid={`edit-review-${review.id}`}
                        className="text-primary hover:text-primary/80"
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleDelete(review.id)}
                        data-testid={`delete-review-${review.id}`}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminReviews;